import { api } from './api';

// User types
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'Admin' | 'Consultant' | 'Pending';
  active: boolean;
  created_at: string;
  updated_at?: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  message: string;
}

export interface UserCreate {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  role?: 'Admin' | 'Consultant' | 'Pending';
  active?: boolean;
}

export interface UserUpdate {
  first_name?: string;
  last_name?: string;
  role?: 'Admin' | 'Consultant' | 'Pending';
  active?: boolean;
}

export interface PasswordChange {
  current_password: string;
  new_password: string;
}

export interface RoleCheckResponse {
  user_id: string;
  email: string;
  role: 'Admin' | 'Consultant' | 'Pending';
  active: boolean;
  has_access: boolean;
  message: string;
}

// User Authentication API
export const userAuthApi = {
  // Authentication
  login: (credentials: LoginCredentials): Promise<LoginResponse> =>
    api.post<LoginResponse>('/auth/login', credentials),

  register: (userData: UserCreate): Promise<User> =>
    api.post<User>('/auth/register', userData),

  // NOTE: there is no /auth/me on this backend. The only auth routes are
  // POST /auth/login and POST /auth/refresh-token. A persisted session is
  // restored by decoding the stored JWT — see lib/authToken.ts.

  // User Management
  getAllUsers: (activeOnly?: boolean): Promise<User[]> =>
    api.get<User[]>('/users', { active_only: activeOnly }),

  getUserById: (userId: string): Promise<User> =>
    api.get<User>(`/users/${userId}`),

  updateUser: (userId: string, userData: UserUpdate): Promise<User> =>
    api.put<User>(`/users/${userId}`, userData),

  deleteUser: (userId: string): Promise<{ message: string; user_id: string }> =>
    api.delete<{ message: string; user_id: string }>(`/users/${userId}`),

  changePassword: (userId: string, passwords: PasswordChange): Promise<{ message: string }> =>
    api.post<{ message: string }>(`/users/${userId}/change-password`, passwords),

  checkUserRole: (userId: string, requiredRole?: string): Promise<RoleCheckResponse> =>
    api.get<RoleCheckResponse>(`/users/${userId}/role-check`, { required_role: requiredRole }),

  // Schema initialization
  initializeSchema: (): Promise<{
    message: string;
    constraints: string[];
    indexes: string[];
  }> => api.post('/users/init-schema'),
};
